import type {IconName} from '@wandb/components';

/**
 * Generic contract for agent UI actions — user-playable buttons the agent emits
 * inline in a turn. The agent calls an action tool, the backend acks it
 * fire-and-forget (see the backend `ui_actions.py`), and the call lands in the
 * turn trajectory as a normal tool call; we recognize those calls by name and
 * render them as re-clickable buttons running a purely client-side effect.
 *
 * This module is the generic framework. The concrete catalog of known actions
 * lives in `./knownActions`; each action defines its own `AgentActionDef`.
 *
 * The button caption prefers the agent-provided `label` arg; `AgentActionDef.label`
 * is the fallback derived from the action's args.
 */
export type AgentActionArgs = Record<string, unknown>;

export interface AgentActionDef {
  /** Backend tool name (wbui__ namespaced); the catalog keys on it. */
  toolName: string;
  /** Action-type name shown in bold on the row, e.g. "Run Filter". */
  name: string;
  icon: IconName;
  /** Fallback title (shown after the name) when the agent gives no `label` arg. */
  label: (args: AgentActionArgs) => string;
  /** Omitted for page-scoped actions, which supply a handler at mount time. */
  execute?: (args: AgentActionArgs) => void;
}

export function parseAgentActionArgs(argsText?: string): AgentActionArgs {
  if (!argsText) {
    return {};
  }
  try {
    const parsed = JSON.parse(argsText);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}
